/** Notes as a parsed body that never runs scripts or loads images. */
function inertBody(html: string): HTMLElement {
  return new DOMParser().parseFromString(html, 'text/html').body
}

/**
 * Strips HTML tags and decodes HTML entities from a string
 * Used for displaying HTML content as plain text in notes
 */
export function stripHtml(html: string): string {
  // Parse in an inert document: a live-document element would load <img>
  // and run its onerror (review 2026-10-04).
  const temp = inertBody(html)

  // Get text content (this handles HTML entity decoding automatically)
  const text = temp.textContent || temp.innerText || ''

  // Clean up extra whitespace
  return text.replace(/\s+/g, ' ').trim()
}

/**
 * Converts HTML content to plain text with preserved line breaks
 * Replaces block elements with newlines for better formatting
 */
export function htmlToPlainText(html: string): string {
  const temp = inertBody(html)

  // Replace block-level elements with newlines before getting text
  const blockElements = temp.querySelectorAll('p, div, br, li, h1, h2, h3, h4, h5, h6')
  blockElements.forEach((el) => {
    if (el.tagName === 'BR') {
      el.replaceWith('\n')
    } else {
      const text = el.textContent || ''
      el.replaceWith(text + '\n')
    }
  })

  // Get final text content
  const text = temp.textContent || temp.innerText || ''

  // Clean up excessive newlines (max 2 consecutive)
  return text
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+/g, ' ') // Normalize spaces but keep newlines
    .trim()
}

/** A task's notes as text: the editor stores HTML, older notes are plain.
 *  (Walkthrough 2026-10-04: a planning card showed "<ul><li><p>Called…".) */
export function notesAsText(notes: string | undefined | null): string {
  if (!notes?.trim()) return ''
  return (/<[a-z][\s\S]*>/i.test(notes) ? htmlToPlainText(notes) : notes).trim()
}
